import React, { useState, useEffect } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { NAKSHATRAS, calculateLivePlanetaryEphemeris } from '../utils/vedicEngine';
import { getDailyPanchangaFactors } from '../utils/muhurtaEngine';
import { getNakshatraName } from '../utils/language';
import { 
  NAKSHATRA_CLASSIFICATIONS, 
  getSpecialSensitiveRole,
  SPECIAL_SENSITIVE_NAKSHATRAS
} from '../utils/nakshatraClassifications';
import { NakshatraHoverCard } from './NakshatraHoverCard';
import { 
  HelpCircle, 
  Sparkles, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldCheck, 
  Clock, 
  Compass, 
  Info,
  Calendar,
  Zap,
  ArrowRight,
  Flame,
  Wind,
  Anchor,
  Activity,
  Layers,
  Check,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  Timer,
  BookOpen,
  Crown
} from 'lucide-react';

interface NavtaraViewProps {
  kundali: CompleteKundali;
  lang?: Language | string;
}

export interface TaraDefinition {
  index: number;
  name: string;
  nameHi: string;
  type: 'benefic' | 'malefic' | 'sensitive';
  typeLabel: string;
  typeLabelHi: string;
  meaning: string;
  meaningHi: string;
  recommendation: string;
  recommendationHi: string;
}

export const TARA_DEFINITIONS: TaraDefinition[] = [
  {
    index: 0,
    name: 'Janma',
    nameHi: 'जन्म तारा',
    type: 'sensitive',
    typeLabel: 'Sensitive / Body',
    typeLabelHi: 'संवेदनशील (देह व मन)',
    meaning: 'Birth Star (Body, Vitality & Mind)',
    meaningHi: 'जन्म नक्षत्र (शरीर, स्वास्थ्य व आत्मा)',
    recommendation: 'Ideal for meditation, introspection and spiritual worship. Avoid high-risk physical or financial ventures.',
    recommendationHi: 'ध्यान, साधना व आत्म-मंथन हेतु उत्तम। जोखिम भरे नए कार्यों व अत्यधिक तनाव से बचें।'
  },
  {
    index: 1,
    name: 'Sampat',
    nameHi: 'सम्पत तारा',
    type: 'benefic',
    typeLabel: 'Benefic (Wealth & Prosperity)',
    typeLabelHi: 'शुभ (धन व समृद्धि)',
    meaning: 'Wealth, Fortune & Prosperity',
    meaningHi: 'धन, संपत्ति, लाभ व ऐश्वर्य',
    recommendation: 'Highly favorable for investments, signing deals, starting business ventures, and purchasing assets.',
    recommendationHi: 'धन निवेश, नवीन व्यापार शुभारंभ, संपत्ति क्रय व वित्तीय सौदों के लिए परम शुभ।'
  },
  {
    index: 2,
    name: 'Vipat',
    nameHi: 'विपत तारा',
    type: 'malefic',
    typeLabel: 'Malefic (Danger & Obstacles)',
    typeLabelHi: 'अशुभ (विपत्ति व अड़चनें)',
    meaning: 'Obstacles, Hazards & Sudden Loss',
    meaningHi: 'रुकावटें, अप्रत्याशित संकट व हानि',
    recommendation: 'Avoid initiating new enterprises, traveling, lending money, or signing major contracts.',
    recommendationHi: 'नए उपक्रम, यात्रा, उधार लेनदेन व महत्वपूर्ण अनुबंधों से परहेज करें। शांति पाठ करें।'
  },
  {
    index: 3,
    name: 'Kshema',
    nameHi: 'क्षेम तारा',
    type: 'benefic',
    typeLabel: 'Benefic (Security & Well-Being)',
    typeLabelHi: 'शुभ (कल्याण व सुरक्षा)',
    meaning: 'Protection, Comfort & Stability',
    meaningHi: 'क्षेम, आरोग्य, सुख व पारिवारिक शांति',
    recommendation: 'Excellent for healing treatments, property purchase, Griha Pravesh, and starting stable ventures.',
    recommendationHi: 'आरोग्य लाभ, संपत्ति क्रय, गृह प्रवेश, पारिवारिक उत्सव व स्थायी कार्यों हेतु श्रेष्ठ।'
  },
  {
    index: 4,
    name: 'Pratyari',
    nameHi: 'प्रत्यरि तारा',
    type: 'malefic',
    typeLabel: 'Malefic (Enemies & Conflicts)',
    typeLabelHi: 'अशुभ (शत्रुता व विरोध)',
    meaning: 'Hostility, Rivalry & Opposition',
    meaningHi: 'शत्रु बाधा, विवाद व असहयोग',
    recommendation: 'Steer clear of legal litigation, heated arguments, competitive disputes, or risky partnerships.',
    recommendationHi: 'मुकदमेबाजी, बहस, विवाद व जोखिम भरी साझेदारियों से दूर रहें।'
  },
  {
    index: 5,
    name: 'Sadhana / Sadhaka',
    nameHi: 'साधना / साधक तारा',
    type: 'benefic',
    typeLabel: 'Benefic (Accomplishment & Mastery)',
    typeLabelHi: 'शुभ (कार्य सिद्धि व साधना)',
    meaning: 'Achievement, Fruition & Spiritual Depth',
    meaningHi: 'सिद्धि, अभीष्ट फल की प्राप्ति व साधना',
    recommendation: 'Ideal for mantra sadhana, examination success, executing strategic plans, and finalizing targets.',
    recommendationHi: 'मंत्र साधना, प्रतियोगी परीक्षा, लक्ष्य सिद्धि व महत्वपूर्ण परियोजनाओं को पूर्ण करने हेतु उत्तम।'
  },
  {
    index: 6,
    name: 'Vadha / Naidhana',
    nameHi: 'वध / नैधन तारा',
    type: 'malefic',
    typeLabel: 'Severe Malefic (Destruction & Endings)',
    typeLabelHi: 'अति अशुभ (वध व गंभीर संकट)',
    meaning: 'Critical Loss, Danger & Termination',
    meaningHi: 'गंभीर कष्ट, हानि व संकट काल',
    recommendation: 'Most challenging star transit. Strictly avoid surgical interventions, risky travels, and fresh capital deployment.',
    recommendationHi: 'अत्यंत संवेदनशील समय। नए आरंभ, शल्य चिकित्सा व भारी खर्च से बचें। महामृत्युंजय मंत्र जपें।'
  },
  {
    index: 7,
    name: 'Mitra',
    nameHi: 'मित्र तारा',
    type: 'benefic',
    typeLabel: 'Benefic (Friendly & Supportive)',
    typeLabelHi: 'शुभ (मित्रता व सौहार्द)',
    meaning: 'Friendship, Joy & Companionship',
    meaningHi: 'सौहार्द, मैत्री, आनंद व सहयोग',
    recommendation: 'Wonderful for networking, socializing, meeting friends, romantic dates, and artistic work.',
    recommendationHi: 'मित्र मिलन, नेटवर्किंग, साझेदारी चर्चा, सामाजिक संबंध व कलात्मक कार्यों हेतु शुभ।'
  },
  {
    index: 8,
    name: 'Ati Mitra',
    nameHi: 'अति मित्र तारा',
    type: 'benefic',
    typeLabel: 'Highly Auspicious (Best Friend)',
    typeLabelHi: 'परम शुभ (अति मित्र व आनंद)',
    meaning: 'Supreme Friendliness, Gains & Bliss',
    meaningHi: 'परम कल्याण, प्रगाढ़ मैत्री व सर्वतोमुखी लाभ',
    recommendation: 'Most auspicious star transit. Excellent for major celebrations, key milestones, and signing agreements.',
    recommendationHi: 'सर्वोत्तम व परम कल्याणकारी। समस्त शुभ कार्यों, उत्सवों, पदभार ग्रहण व बड़े समझौतों हेतु उपयुक्त।'
  }
];

export const NavtaraView: React.FC<NavtaraViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';
  const [selectedTara, setSelectedTara] = useState<TaraDefinition | null>(null);

  // Filter toggles
  const [selectedCycleFilter, setSelectedCycleFilter] = useState<'all' | 'cycle1' | 'cycle2' | 'cycle3'>('all');
  const [selectedNatureFilter, setSelectedNatureFilter] = useState<'all' | 'chara' | 'sthira' | 'dvisvabhava' | 'kshipra' | 'ugra' | 'tikshna'>('all');
  const [showRahuKalamAlert, setShowRahuKalamAlert] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_rahu_kalam_pref') !== 'false';
  });
  const [showMotionBadges, setShowMotionBadges] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_motion_badges_pref') !== 'false';
  });
  const [hoverEnabled, setHoverEnabled] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_hover_pref') !== 'false';
  });
  const [flashActiveWindowPref, setFlashActiveWindowPref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_flash_pref') !== 'false';
  });
  const [isCurrentlyFlashing, setIsCurrentlyFlashing] = useState<boolean>(false);
  const [sessionFlashDismissed, setSessionFlashDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('astro_navtara_flash_dismissed_session') === 'true';
    } catch {
      return false;
    }
  });

  // Collapsible sections state
  const [isGuideExpanded, setIsGuideExpanded] = useState<boolean>(true);
  const [guideSecondsRemaining, setGuideSecondsRemaining] = useState<number>(25);
  const [isGuideHovered, setIsGuideHovered] = useState<boolean>(false);
  const [hasUserManuallyToggledGuide, setHasUserManuallyToggledGuide] = useState<boolean>(false);

  // Navtara specific feature settings
  const [deductRahuKalam, setDeductRahuKalam] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_deduct_rahu') !== 'false';
  });

  const [autoCollapseGuidePref, setAutoCollapseGuidePref] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_guide_autocollapse') !== 'false';
  });

  const [hideCurrentInTable, setHideCurrentInTable] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_hide_current_in_table') !== 'false';
  });

  const [isTableExpanded, setIsTableExpanded] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_table_expanded') !== 'false';
  });

  const [isSensitiveExpanded, setIsSensitiveExpanded] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_sensitive_expanded') !== 'false';
  });

  // Switchable Nakshatra Lord & Deity details line ("Lord: Sun • Aryaman (God of Patronage & Contracts)")
  const [showLordDeityDetails, setShowLordDeityDetails] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_show_lord_deity') !== 'false';
  });

  const [showSensitivePoints, setShowSensitivePoints] = useState<boolean>(() => {
    return localStorage.getItem('astro_navtara_show_sensitive_points') === 'true';
  });

  // Auto-collapse Benefic & Malefic Guide after sufficient reading time (25 seconds), paused when hovering
  useEffect(() => {
    if (!autoCollapseGuidePref || !isGuideExpanded || hasUserManuallyToggledGuide || guideSecondsRemaining <= 0) {
      return;
    }

    const timer = setInterval(() => {
      if (!isGuideHovered) {
        setGuideSecondsRemaining((prev) => {
          if (prev <= 1) {
            setIsGuideExpanded(false);
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [autoCollapseGuidePref, isGuideExpanded, hasUserManuallyToggledGuide, guideSecondsRemaining, isGuideHovered]);

  // Periodic flashing effect: flash a few times every few minutes when enabled and not dismissed for session
  useEffect(() => {
    if (!flashActiveWindowPref || sessionFlashDismissed) {
      setIsCurrentlyFlashing(false);
      return;
    }

    // Trigger an initial burst of flashes on mount/enable if not dismissed
    setIsCurrentlyFlashing(true);
    const initialTimer = setTimeout(() => {
      setIsCurrentlyFlashing(false);
    }, 2400); // 3 gentle pulses (~0.65s each)

    // Repeat every 2.5 minutes (150,000 ms) unless dismissed
    const interval = setInterval(() => {
      try {
        if (sessionStorage.getItem('astro_navtara_flash_dismissed_session') === 'true') {
          setIsCurrentlyFlashing(false);
          setSessionFlashDismissed(true);
          return;
        }
      } catch {}

      setIsCurrentlyFlashing(true);
      setTimeout(() => {
        setIsCurrentlyFlashing(false);
      }, 2400);
    }, 150000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [flashActiveWindowPref, sessionFlashDismissed]);

  // Listen to storage events for settings changes
  useEffect(() => {
    const handleSettingsChange = () => {
      setHoverEnabled(localStorage.getItem('astro_navtara_hover_pref') !== 'false');
    };
    const handleFlashSettingsChange = () => {
      setFlashActiveWindowPref(localStorage.getItem('astro_navtara_flash_pref') !== 'false');
    };
    const handleDeductRahuChange = () => {
      setDeductRahuKalam(localStorage.getItem('astro_navtara_deduct_rahu') !== 'false');
    };
    const handleGuideAutoCollapseChange = () => {
      setAutoCollapseGuidePref(localStorage.getItem('astro_navtara_guide_autocollapse') !== 'false');
    };
    const handleMotionBadgesChange = () => {
      setShowMotionBadges(localStorage.getItem('astro_navtara_motion_badges_pref') !== 'false');
    };
    const handleLordDeityChange = () => {
      setShowLordDeityDetails(localStorage.getItem('astro_navtara_show_lord_deity') !== 'false');
    };
    const handleSensitivePointsChange = () => {
      setShowSensitivePoints(localStorage.getItem('astro_navtara_show_sensitive_points') === 'true');
    };
    window.addEventListener('astro_navtara_hover_pref_changed', handleSettingsChange);
    window.addEventListener('astro_navtara_flash_pref_changed', handleFlashSettingsChange);
    window.addEventListener('astro_navtara_deduct_rahu_changed', handleDeductRahuChange);
    window.addEventListener('astro_navtara_guide_autocollapse_changed', handleGuideAutoCollapseChange);
    window.addEventListener('astro_navtara_motion_badges_pref_changed', handleMotionBadgesChange);
    window.addEventListener('astro_navtara_show_lord_deity_changed', handleLordDeityChange);
    window.addEventListener('astro_navtara_show_sensitive_points_changed', handleSensitivePointsChange);
    return () => {
      window.removeEventListener('astro_navtara_hover_pref_changed', handleSettingsChange);
      window.removeEventListener('astro_navtara_flash_pref_changed', handleFlashSettingsChange);
      window.removeEventListener('astro_navtara_deduct_rahu_changed', handleDeductRahuChange);
      window.removeEventListener('astro_navtara_guide_autocollapse_changed', handleGuideAutoCollapseChange);
      window.removeEventListener('astro_navtara_motion_badges_pref_changed', handleMotionBadgesChange);
      window.removeEventListener('astro_navtara_show_lord_deity_changed', handleLordDeityChange);
      window.removeEventListener('astro_navtara_show_sensitive_points_changed', handleSensitivePointsChange);
    };
  }, []);

  const handleToggleShowLordDeity = (val: boolean) => {
    setShowLordDeityDetails(val);
    try {
      localStorage.setItem('astro_navtara_show_lord_deity', String(val));
      window.dispatchEvent(new Event('astro_navtara_show_lord_deity_changed'));
    } catch {}
  };

  const handleToggleShowSensitivePoints = (val: boolean) => {
    setShowSensitivePoints(val);
    try {
      localStorage.setItem('astro_navtara_show_sensitive_points', String(val));
      window.dispatchEvent(new Event('astro_navtara_show_sensitive_points_changed'));
    } catch {}
  };

  const handleToggleDeductRahu = (val: boolean) => {
    setDeductRahuKalam(val);
    try {
      localStorage.setItem('astro_navtara_deduct_rahu', String(val));
      window.dispatchEvent(new Event('astro_navtara_deduct_rahu_changed'));
    } catch {}
  };

  const handleToggleHideCurrentInTable = (val: boolean) => {
    setHideCurrentInTable(val);
    try {
      localStorage.setItem('astro_navtara_hide_current_in_table', String(val));
    } catch {}
  };

  const handleToggleRahuKalam = (val: boolean) => {
    setShowRahuKalamAlert(val);
    try {
      localStorage.setItem('astro_navtara_rahu_kalam_pref', String(val));
    } catch {}
  };

  const handleToggleFlashActive = (val: boolean) => {
    setFlashActiveWindowPref(val);
    try {
      localStorage.setItem('astro_navtara_flash_pref', String(val));
      window.dispatchEvent(new Event('astro_navtara_flash_pref_changed'));
    } catch {}
  };

  const handleToggleMotionBadges = (val: boolean) => {
    setShowMotionBadges(val);
    try {
      localStorage.setItem('astro_navtara_motion_badges_pref', String(val));
    } catch {}
  };

  const handleDismissFlashSession = () => {
    setIsCurrentlyFlashing(false);
    setSessionFlashDismissed(true);
    try {
      sessionStorage.setItem('astro_navtara_flash_dismissed_session', 'true');
    } catch {}
  };

  const handleToggleTableExpand = () => {
    const next = !isTableExpanded;
    setIsTableExpanded(next);
    try {
      localStorage.setItem('astro_navtara_table_expanded', String(next));
    } catch {}
  };

  const handleToggleSensitiveExpand = () => {
    const next = !isSensitiveExpanded;
    setIsSensitiveExpanded(next);
    try {
      localStorage.setItem('astro_navtara_sensitive_expanded', String(next));
    } catch {}
  };

  const handleToggleHoverPref = (val: boolean) => {
    setHoverEnabled(val);
    try {
      localStorage.setItem('astro_navtara_hover_pref', String(val));
      window.dispatchEvent(new Event('astro_navtara_hover_pref_changed'));
    } catch {}
  };

  const moon = kundali.planets.find((p) => p.name === 'Moon');

  if (!moon) {
    return (
      <div className="p-8 text-center text-astro-secondary-text bg-astro-bg rounded-2xl border border-astro-border">
        {isHi ? 'चंद्रमा का नक्षत्र विवरण उपलब्ध नहीं है।' : 'Moon Nakshatra data is not available.'}
      </div>
    );
  }

  const janmaIndex = moon.nakshatraIndex; // 0-26
  const janmaNakshatraObj = NAKSHATRAS[janmaIndex];

  // Calculate live moon transit nakshatra
  const now = new Date();
  const liveEphem = calculateLivePlanetaryEphemeris(now);
  const liveMoon = liveEphem.find((p) => p.planet === 'Moon');
  const liveMoonLon = liveMoon ? liveMoon.longitude : 0;
  const currentMoonNakIndex = Math.floor(liveMoonLon / (360 / 27));

  // Calculate live location panchang & Rahu Kalam
  const lat = kundali.birthDetails?.latitude || 28.6139;
  const lon = kundali.birthDetails?.longitude || 77.2090;
  const panchang = getDailyPanchangaFactors(now, lat, lon);
  const isCurrentlyInRahuKalam = panchang?.rahuKalam?.rawStart && panchang?.rahuKalam?.rawEnd
    ? now >= panchang.rahuKalam.rawStart && now <= panchang.rahuKalam.rawEnd
    : false;

  // Date and Time formatter including 12-hour AM/PM clock timing, 2-digit year ('26) and IST
  const formatTransitDateTime = (d: Date): string => {
    const day = d.getDate().toString().padStart(2, '0');
    const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNamesHi = ['जन', 'फर', 'मार्च', 'अप्रै', 'मई', 'जून', 'जुला', 'अग', 'सित', 'अक्तू', 'नव', 'दिस'];
    const month = isHi ? monthNamesHi[d.getMonth()] : monthNamesEn[d.getMonth()];
    const year = d.getFullYear().toString().slice(-2); // "26"

    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const timeStr = `${hours12.toString().padStart(2, '0')}:${minutes} ${ampm}`;

    return `${day} ${month} '${year}, ${timeStr} IST`;
  };

  const getNakshatraTransitStatus = (targetNakIndex: number, taraType: 'benefic' | 'malefic' | 'sensitive') => {
    const nakStartLon = targetNakIndex * (360 / 27);
    const moonDailySpeed = 13.176358; // degrees per day mean speed

    if (currentMoonNakIndex === targetNakIndex) {
      // ACTIVE TRANSIT (Currently Ongoing right now!)
      const degPassed = (liveMoonLon - nakStartLon + 360) % (360 / 27);
      const degRemaining = (360 / 27) - degPassed;

      const daysAgoStarted = degPassed / moonDailySpeed;
      const daysUntilEnd = degRemaining / moonDailySpeed;

      const startDate = new Date(now.getTime() - daysAgoStarted * 24 * 3600 * 1000);
      const endDate = new Date(now.getTime() + daysUntilEnd * 24 * 3600 * 1000);

      const startStr = formatTransitDateTime(startDate);
      const endStr = formatTransitDateTime(endDate);

      return {
        active: true,
        taraType,
        startDate,
        endDate,
        label: isHi ? `✨ सक्रिय: ${startStr} — ${endStr}` : `✨ Active: ${startStr} — ${endStr}`,
        startStr,
        endStr
      };
    }

    // UPCOMING TRANSIT
    let diffToStart = (nakStartLon - liveMoonLon + 360) % 360;
    if (diffToStart === 0) diffToStart = 360;
    const daysToStart = diffToStart / moonDailySpeed;
    const daysToEnd = daysToStart + (360 / 27) / moonDailySpeed;

    const startDate = new Date(now.getTime() + daysToStart * 24 * 3600 * 1000);
    const endDate = new Date(now.getTime() + daysToEnd * 24 * 3600 * 1000);

    const startStr = formatTransitDateTime(startDate);
    const endStr = formatTransitDateTime(endDate);

    return {
      active: false,
      taraType,
      startDate,
      endDate,
      label: isHi ? `आगामी: ${startStr} — ${endStr}` : `Next: ${startStr} — ${endStr}`,
      startStr,
      endStr
    };
  };

  // Find currently active star in the 9-fold grid
  const currentNakObj = NAKSHATRAS[currentMoonNakIndex];
  const currentNakOffset = (currentMoonNakIndex - janmaIndex + 27) % 27;
  const currentTaraIndex = currentNakOffset % 9;
  const currentCycleNumber = Math.floor(currentNakOffset / 9) + 1;
  const currentTaraDef = TARA_DEFINITIONS[currentTaraIndex];
  const currentClassification = NAKSHATRA_CLASSIFICATIONS[currentMoonNakIndex];
  const activeStatus = getNakshatraTransitStatus(currentMoonNakIndex, currentTaraDef.type);

  // Net Auspicious Window calculation (deducting Rahu Kalam from benefic stars)
  const isBeneficTransit = currentTaraDef.type === 'benefic';
  const grossTransitRemainingMs = Math.max(0, activeStatus.endDate.getTime() - now.getTime());
  const grossHours = Math.floor(grossTransitRemainingMs / (1000 * 60 * 60));
  const grossMins = Math.floor((grossTransitRemainingMs % (1000 * 60 * 60)) / (1000 * 60));

  let remainingRahuOverlapMs = 0;
  let rahuKalamTotalMins = 90;
  const hasRahuKalam = Boolean(panchang?.rahuKalam?.rawStart && panchang?.rahuKalam?.rawEnd);

  if (hasRahuKalam && panchang?.rahuKalam?.rawStart && panchang?.rahuKalam?.rawEnd) {
    const rStart = panchang.rahuKalam.rawStart.getTime();
    const rEnd = panchang.rahuKalam.rawEnd.getTime();
    rahuKalamTotalMins = Math.max(1, Math.round((rEnd - rStart) / 60000));

    const overlapStart = Math.max(now.getTime(), rStart);
    const overlapEnd = Math.min(activeStatus.endDate.getTime(), rEnd);
    if (overlapEnd > overlapStart) {
      remainingRahuOverlapMs = overlapEnd - overlapStart;
    }
  }

  const remainingRahuOverlapMins = Math.round(remainingRahuOverlapMs / 60000);
  const netAuspiciousMs = Math.max(
    0,
    grossTransitRemainingMs - (deductRahuKalam && isBeneficTransit ? remainingRahuOverlapMs : 0)
  );
  const netHours = Math.floor(netAuspiciousMs / (1000 * 60 * 60));
  const netMins = Math.floor((netAuspiciousMs % (1000 * 60 * 60)) / (1000 * 60));

  // Collect and sort all 27 nakshatras by upcoming transit time for timeline summary
  const allTransits = Array.from({ length: 27 }, (_, i) => {
    const nakIndex = (currentMoonNakIndex + i) % 27;
    const nakObj = NAKSHATRAS[nakIndex];
    const nakOffset = (nakIndex - janmaIndex + 27) % 27;
    const taraIdx = nakOffset % 9;
    const cycleNum = Math.floor(nakOffset / 9) + 1;
    const taraDef = TARA_DEFINITIONS[taraIdx];
    const status = getNakshatraTransitStatus(nakIndex, taraDef.type);
    const classification = NAKSHATRA_CLASSIFICATIONS[nakIndex];
    return {
      nakIndex,
      nakName: getNakshatraName(nakObj.name, isHi ? 'hi' : 'en'),
      lord: nakObj.lord,
      taraDef,
      cycleNum,
      status,
      classification
    };
  });

  const getMotionChip = (type: string, isSmall = true) => {
    switch (type) {
      case 'chara':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded font-bold border ${
            isSmall ? 'text-[9px] px-1.5 py-0.2' : 'text-[10px] px-2 py-0.5'
          } bg-sky-50 text-sky-900 border-sky-300 shadow-2xs`}>
            <span className="relative flex h-1.5 w-1.5 items-center justify-center shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sky-600" />
            </span>
            <Wind className="w-2.5 h-2.5 text-sky-600 shrink-0" />
            <span className="animate-pulse">{isHi ? 'चर (Chara)' : 'Chara (Movable)'}</span>
          </span>
        );
      case 'sthira':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded font-bold border ${
            isSmall ? 'text-[9px] px-1.5 py-0.2' : 'text-[10px] px-2 py-0.5'
          } bg-emerald-50 text-emerald-950 border-emerald-300 shadow-2xs`}>
            <span className="relative flex h-1.5 w-1.5 items-center justify-center shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600" />
            </span>
            <Anchor className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
            <span className="animate-pulse">{isHi ? 'स्थिर / अचल' : 'Sthira (Fixed)'}</span>
          </span>
        );
      case 'dvisvabhava':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded font-bold border ${
            isSmall ? 'text-[9px] px-1.5 py-0.2' : 'text-[10px] px-2 py-0.5'
          } bg-purple-50 text-purple-900 border-purple-300 shadow-2xs`}>
            <span className="relative flex h-1.5 w-1.5 items-center justify-center shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-600" />
            </span>
            <Activity className="w-2.5 h-2.5 text-purple-600 shrink-0" />
            <span className="animate-pulse">{isHi ? 'द्वि-स्वभाव' : 'Dvisvabhava (Dual)'}</span>
          </span>
        );
      case 'kshipra':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded font-bold border ${
            isSmall ? 'text-[9px] px-1.5 py-0.2' : 'text-[10px] px-2 py-0.5'
          } bg-amber-50 text-amber-950 border-amber-300 shadow-2xs`}>
            <span className="relative flex h-1.5 w-1.5 items-center justify-center shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-600" />
            </span>
            <Sparkles className="w-2.5 h-2.5 text-amber-600 shrink-0" />
            <span className="animate-pulse">{isHi ? 'लघु / क्षिप्र' : 'Kshipra (Swift)'}</span>
          </span>
        );
      case 'ugra':
      case 'tikshna':
        return (
          <span className={`inline-flex items-center gap-1.5 rounded font-bold border ${
            isSmall ? 'text-[9px] px-1.5 py-0.2' : 'text-[10px] px-2 py-0.5'
          } bg-rose-50 text-rose-950 border-rose-300 shadow-2xs`}>
            <span className="relative flex h-1.5 w-1.5 items-center justify-center shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-600" />
            </span>
            <Flame className="w-2.5 h-2.5 text-rose-600 shrink-0" />
            <span className="animate-pulse">{isHi ? 'उग्र / तीक्ष्ण' : 'Ugra / Tikshna'}</span>
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Vedic Profile Details */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-astro-border pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-astro-primary text-astro-bg flex items-center justify-center shadow-xs">
              <Compass className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-serif text-astro-primary">
                {isHi ? 'नव तारा चक्र' : 'Navatara Chakra'} — {kundali.birthDetails?.name || (isHi ? 'जातक' : 'Native')}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi 
                  ? 'जन्म नक्षत्र के सापेक्ष 27 नक्षत्रों का 9-चरणीय शुभाशुभ काल, चल/अचल वर्गीकरण एवं राहुकाल समन्वय'
                  : '9-Fold Auspiciousness Matrix, Chala/Achala/Dvisvabhava Nature & Rahu Kalam Synchronization'}
              </p>
            </div>
          </div>

          {/* Quick Feature Toggles Bar */}
          <div className="flex items-center flex-wrap gap-2">
            {/* Nakshatra Click Insights Toggle */}
            <button
              type="button"
              onClick={() => handleToggleHoverPref(!hoverEnabled)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                hoverEnabled
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={isHi ? 'नक्षत्रों पर क्लिक अंतर्दृष्टि कार्ड चालू/बंद करें' : 'Toggle Nakshatra Detail Cards'}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isHi ? 'नक्षत्र अंतर्दृष्टि' : 'Nakshatra Info'}: {hoverEnabled ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')}</span>
            </button>

            {/* Deduct Rahu Kalam Toggle */}
            <button
              type="button"
              onClick={() => handleToggleDeductRahu(!deductRahuKalam)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                deductRahuKalam
                  ? 'bg-amber-800 text-white border-amber-800 shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={isHi ? 'सक्रिय शुभ तारा में से राहुकाल घटाएं' : 'Deduct Rahu Kalam from Benefic Transit duration'}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>{isHi ? 'राहुकाल कटौती' : 'Deduct Rahu'}: {deductRahuKalam ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')}</span>
            </button>

            {/* Flash Active Window Toggle */}
            <button
              type="button"
              onClick={() => {
                if (sessionFlashDismissed) {
                  setSessionFlashDismissed(false);
                  try {
                    sessionStorage.removeItem('astro_navtara_flash_dismissed_session');
                  } catch {}
                  handleToggleFlashActive(true);
                } else {
                  handleToggleFlashActive(!flashActiveWindowPref);
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                flashActiveWindowPref && !sessionFlashDismissed
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={
                sessionFlashDismissed
                  ? (isHi ? 'स्पंदन इस सत्र हेतु बंद है। पुनः चालू करने हेतु क्लिक करें।' : 'Flashing dismissed for this session. Click to re-enable.')
                  : (isHi ? 'सारणी में सक्रिय नक्षत्र के स्पंदन को चालू/बंद करें' : 'Toggle Periodic Flashing of Table Active Transit Time')
              }
            >
              <Zap className={`w-3.5 h-3.5 ${flashActiveWindowPref && !sessionFlashDismissed ? 'text-amber-300' : ''}`} />
              <span>
                {isHi ? 'स्पंदन प्रभाव' : 'Flash Active'}: {
                  sessionFlashDismissed
                    ? (isHi ? 'सत्र में बंद' : 'Dismissed')
                    : flashActiveWindowPref ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')
                }
              </span>
            </button>

            {/* Rahu Kalam Strip & Deduction Toggle */}
            <button
              type="button"
              onClick={() => handleToggleRahuKalam(!showRahuKalamAlert)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                showRahuKalamAlert
                  ? 'bg-rose-700 text-white border-rose-700 shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={isHi ? 'राहुकाल चेतावनी व समय पट्टी चालू अथवा बंद करें' : 'Toggle Rahu Kalam Timing & In-Transit Alert Strip'}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>{isHi ? 'राहुकाल' : 'Rahu Kalam'}: {showRahuKalamAlert ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')}</span>
            </button>

            {/* Motion Badges Toggle */}
            <button
              type="button"
              onClick={() => handleToggleMotionBadges(!showMotionBadges)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                showMotionBadges
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={isHi ? 'चर, अचल व द्वि-स्वभाव बैज प्रदर्शित करें' : 'Toggle Chala / Achala / Dvisvabhava Motion Badges'}
            >
              <Compass className="w-3.5 h-3.5" />
              <span>{isHi ? 'गति बैज' : 'Motion'}: {showMotionBadges ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')}</span>
            </button>

            {/* Nakshatra Lord & Deity Info Toggle */}
            <button
              type="button"
              onClick={() => handleToggleShowLordDeity(!showLordDeityDetails)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                showLordDeityDetails
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={isHi ? 'नक्षत्र विवरण (Lord: Sun • Aryaman आदि) दिखाएं अथवा छिपाएं' : 'Toggle Nakshatra details (Lord: Sun • Aryaman...) in cells'}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>{isHi ? 'स्वामी व देवता' : 'Lord & Deity'}: {showLordDeityDetails ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')}</span>
            </button>

            {/* Special Sensitive Points Toggle */}
            <button
              type="button"
              onClick={() => handleToggleShowSensitivePoints(!showSensitivePoints)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
                showSensitivePoints
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                  : 'bg-astro-card text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
              }`}
              title={isHi ? 'पाराशरी विशिष्ट संवेदनशील नक्षत्र तालिका दिखाएं अथवा छिपाएं' : 'Toggle Special Sensitive Nakshatras Table'}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isHi ? 'संवेदनशील बिंदु' : 'Sensitive Points'}: {showSensitivePoints ? (isHi ? 'चालू' : 'ON') : (isHi ? 'बंद' : 'OFF')}</span>
            </button>
          </div>
        </div>

        {/* Info Highlights Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <div className="bg-astro-card p-3.5 rounded-xl border border-astro-border">
            <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block">
              {isHi ? 'जातक का नाम' : 'Native Name'}
            </span>
            <span className="text-sm sm:text-base font-bold text-astro-primary mt-0.5 block truncate">
              {kundali.birthDetails?.name || 'AstroVed Kundali'}
            </span>
          </div>

          <div className="bg-astro-card p-3.5 rounded-xl border border-astro-border">
            <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block">
              {isHi ? 'जन्म नक्षत्र (Janma Nakshatra)' : 'Janma Nakshatra (Base Star)'}
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-sm sm:text-base font-bold text-astro-primary">
                #{janmaIndex + 1} {getNakshatraName(janmaNakshatraObj.name, isHi ? 'hi' : 'en')}
              </span>
              {showMotionBadges && getMotionChip(NAKSHATRA_CLASSIFICATIONS[janmaIndex]?.motionType, true)}
            </div>
          </div>

          <div className="bg-astro-card p-3.5 rounded-xl border border-astro-border">
            <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block">
              {isHi ? 'नक्षत्र स्वामी (Lord)' : 'Nakshatra Lord'}
            </span>
            <span className="text-sm sm:text-base font-bold text-astro-primary mt-0.5 block">
              {janmaNakshatraObj.lord}
            </span>
          </div>
        </div>

        {/* Streamlined Rahu Kalam & Panchang Schedule Strip */}
        {showRahuKalamAlert && hasRahuKalam && panchang?.rahuKalam && (
          <div className={`p-3.5 rounded-xl border text-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
            isCurrentlyInRahuKalam 
              ? 'bg-rose-100/90 border-rose-400 text-rose-950 ring-2 ring-rose-300' 
              : 'bg-astro-card border-astro-border text-astro-primary'
          }`}>
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                isCurrentlyInRahuKalam ? 'bg-rose-600 text-white' : 'bg-astro-secondary/10 text-astro-primary'
              }`}>
                <Clock className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-xs uppercase tracking-wide">
                    {isHi ? 'आज का राहुकाल (Rahu Kalam Today)' : 'Rahu Kalam Timing Today'}
                  </span>
                  {isCurrentlyInRahuKalam && (
                    <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white font-extrabold text-[10px] animate-pulse">
                      {isHi ? '⚠️ वर्तमान में सक्रिय' : '⚠️ ACTIVE NOW'}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-astro-secondary-text mt-0.5">
                  <span className="font-bold text-astro-primary">{panchang.rahuKalam.start} — {panchang.rahuKalam.end}</span> ({panchang.vara})
                  {' • '}{isHi ? 'शुभ कार्यों के आरंभ हेतु राहुकाल अवधि त्याज्य है।' : 'Classical rule: Auspicious beginnings should be timed outside Rahu Kalam.'}
                </p>
              </div>
            </div>

            <div className="shrink-0 flex items-center gap-3 text-[11px]">
              {panchang.gulikaKalam && (
                <div className="text-right">
                  <span className="text-astro-secondary-text block">{isHi ? 'गुलिक काल' : 'Gulika'}:</span>
                  <span className="font-bold text-astro-primary">{panchang.gulikaKalam.start} — {panchang.gulikaKalam.end}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live Active Moon Transit Highlight Box with Net Auspicious Time Calculation */}
        <div 
          id="active-nakshatra-transit-window"
          className={`p-4 sm:p-5 rounded-xl border-2 shadow-xs transition-all duration-300 ${
            currentTaraDef.type === 'benefic'
              ? 'bg-emerald-50/90 border-emerald-300 text-emerald-950'
              : currentTaraDef.type === 'malefic'
              ? 'bg-rose-50/90 border-rose-300 text-rose-950'
              : 'bg-amber-50/90 border-amber-300 text-amber-950'
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 shadow-xs ${
                currentTaraDef.type === 'benefic'
                  ? 'bg-emerald-600 text-white'
                  : currentTaraDef.type === 'malefic'
                  ? 'bg-rose-600 text-white'
                  : 'bg-amber-600 text-white'
              }`}>
                <Zap className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Dynamic Flashing Dot & Pulsing Text based on Tara & Nakshatra Quality */}
                  <span className={`text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full border shadow-2xs flex items-center gap-1.5 ${
                    currentTaraDef.type === 'benefic'
                      ? 'bg-emerald-50 text-emerald-950 border-emerald-300'
                      : currentTaraDef.type === 'malefic'
                      ? currentTaraDef.index === 6
                        ? 'bg-rose-50 text-rose-950 border-rose-300'
                        : 'bg-amber-50 text-amber-950 border-amber-300'
                      : 'bg-indigo-50 text-indigo-950 border-indigo-300'
                  }`}>
                    <span className="relative flex h-2.5 w-2.5 items-center justify-center shrink-0">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                        currentTaraDef.type === 'benefic'
                          ? 'bg-emerald-400'
                          : currentTaraDef.type === 'malefic'
                          ? currentTaraDef.index === 6 ? 'bg-rose-400' : 'bg-amber-400'
                          : 'bg-indigo-400'
                      }`} />
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${
                        currentTaraDef.type === 'benefic'
                          ? 'bg-emerald-600'
                          : currentTaraDef.type === 'malefic'
                          ? currentTaraDef.index === 6 ? 'bg-rose-600' : 'bg-amber-600'
                          : 'bg-indigo-600'
                      }`} />
                    </span>
                    <span className="animate-pulse">
                      {isHi ? 'वर्तमान सक्रिय गोचर' : 'CURRENT ACTIVE TRANSIT'}
                    </span>
                  </span>
                  
                  <NakshatraHoverCard
                    nakIndex={currentMoonNakIndex}
                    janmaIndex={janmaIndex}
                    isHi={isHi}
                    kundali={kundali}
                    enabled={hoverEnabled}
                  >
                    <span className="text-sm font-extrabold underline decoration-dotted decoration-astro-accent cursor-help hover:text-astro-accent transition-colors">
                      #{currentMoonNakIndex + 1} {getNakshatraName(currentNakObj.name, isHi ? 'hi' : 'en')}
                    </span>
                  </NakshatraHoverCard>

                  {showLordDeityDetails && (
                    <span className="text-xs font-semibold opacity-90">
                      ({isHi ? 'स्वामी:' : 'Lord:'} {currentNakObj.lord} • {isHi ? currentClassification?.deityHi : currentClassification?.deity})
                    </span>
                  )}

                  {showMotionBadges && getMotionChip(currentClassification?.motionType, false)}

                  <span className="text-xs font-semibold opacity-90">
                    • Cycle {currentCycleNumber} ({isHi ? currentTaraDef.nameHi : currentTaraDef.name} Tara)
                  </span>
                </div>

                <p className="text-xs font-medium mt-1 opacity-90">
                  {isHi ? currentTaraDef.recommendationHi : currentTaraDef.recommendation}
                </p>

                {/* Single Non-Redundant Transit Window Display */}
                <div className="flex items-center gap-1.5 mt-1.5 text-[11px] font-semibold opacity-95">
                  <Clock className="w-3.5 h-3.5 shrink-0 opacity-70" />
                  <span>{isHi ? 'सक्रिय गोचर काल:' : 'Transit Window:'}</span>
                  <span className="font-bold underline decoration-astro-accent/50">{activeStatus.startStr} → {activeStatus.endStr}</span>
                </div>
              </div>
            </div>

            {/* Clean Remaining Time Pill (Replaces duplicate start/end label) */}
            <div className="shrink-0 flex items-center gap-2 self-start md:self-auto">
              <div className={`px-3 py-1.5 rounded-lg font-bold text-xs shadow-xs flex items-center gap-1.5 transition-all ${
                currentTaraDef.type === 'benefic'
                  ? 'bg-emerald-700 text-white'
                  : currentTaraDef.type === 'malefic'
                  ? 'bg-rose-700 text-white'
                  : 'bg-amber-700 text-white'
              }`}>
                <Clock className="w-3.5 h-3.5" />
                <span>{grossHours}h {grossMins}m {isHi ? 'शेष' : 'remaining'}</span>
              </div>
            </div>
          </div>

          {/* Net Auspicious Time Calculation with Rahu Kalam Deduction */}
          {isBeneficTransit && (
            <div className="mt-3.5 pt-3.5 border-t border-emerald-300/60 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-extrabold flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/95 shadow-2xs text-emerald-950 border border-emerald-400">
                  <Clock className="w-4 h-4 text-emerald-700" />
                  <span>
                    {deductRahuKalam
                      ? (isHi ? 'शुद्ध शुभ समय (राहुकाल कटौती उपरांत):' : 'Net Clean Auspicious Window:')
                      : (isHi ? 'कुल गोचर समय शेष:' : 'Gross Auspicious Time Remaining:')}
                  </span>
                  <span className="text-sm font-black text-emerald-800">
                    {netHours}h {netMins}m
                  </span>
                </span>

                <div className="text-[11px] font-medium opacity-90 flex items-center gap-2 flex-wrap">
                  <span>
                    {isHi ? 'कुल गोचर शेष:' : 'Gross Remaining:'} {grossHours}h {grossMins}m
                  </span>
                  {deductRahuKalam && hasRahuKalam && remainingRahuOverlapMins > 0 && (
                    <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-900 border border-rose-200 font-bold">
                      {isHi ? 'राहुकाल कटौती:' : 'Rahu Kalam Deducted:'} -{remainingRahuOverlapMins}m ({panchang?.rahuKalam?.start}–{panchang?.rahuKalam?.end})
                    </span>
                  )}
                  {deductRahuKalam && hasRahuKalam && remainingRahuOverlapMins === 0 && (
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold">
                      {isHi ? 'राहुकाल बीत चुका है (100% शुद्ध काल)' : 'Rahu Kalam already passed today (100% clean)'}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleToggleDeductRahu(!deductRahuKalam)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all cursor-pointer shadow-2xs flex items-center gap-1.5 ${
                  deductRahuKalam
                    ? 'bg-emerald-800 text-white border-emerald-800 hover:bg-emerald-900'
                    : 'bg-white/90 text-emerald-900 border-emerald-400 hover:bg-white'
                }`}
                title={isHi ? 'राहुकाल कटौती चालू या बंद करें' : 'Toggle Rahu Kalam deduction on/off'}
              >
                <Clock className="w-3.5 h-3.5" />
                <span>
                  {deductRahuKalam 
                    ? (isHi ? 'राहुकाल कटौती: चालू' : 'Deduct Rahu: ON') 
                    : (isHi ? 'राहुकाल घटाएं: बंद' : 'Deduct Rahu: OFF')}
                </span>
              </button>
            </div>
          )}

          {/* In-progress warning banner if Rahu Kalam is occurring right now during benefic transit */}
          {isBeneficTransit && isCurrentlyInRahuKalam && panchang?.rahuKalam && (
            <div className="mt-2.5 p-2.5 rounded-lg bg-rose-50 border border-rose-300 text-rose-900 text-xs font-bold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>
                {isHi 
                  ? `⚠️ वर्तमान में राहुकाल जारी है (${panchang.rahuKalam.start} — ${panchang.rahuKalam.end})। शुभ तारा गोचर होने पर भी नए आरंभिक कार्य वर्जित हैं। शुद्ध शुभ समय ${panchang.rahuKalam.end} के बाद पुनः शुरू होगा।` 
                  : `⚠️ Rahu Kalam is active now (${panchang.rahuKalam.start} — ${panchang.rahuKalam.end}). Even though this star is benefic, pause new initiations until ${panchang.rahuKalam.end}.`}
              </span>
            </div>
          )}
        </div>

        {/* Benefic / Malefic Tara Color Legend & Motion Guide (Expandable / Collapsible with Reading Timer) */}
        <div 
          onMouseEnter={() => setIsGuideHovered(true)}
          onMouseLeave={() => setIsGuideHovered(false)}
          className="bg-astro-card border border-astro-border rounded-xl p-3.5 text-xs space-y-3 transition-all"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-bold text-astro-primary flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'तारा शुभाशुभ वर्गीकरण (Color Legend)' : 'Navtara Benefic & Malefic Color Guide'}</span>
            </div>

            <div className="flex items-center gap-2">
              {/* Countdown badge if auto-collapsing and expanded */}
              {isGuideExpanded && !hasUserManuallyToggledGuide && guideSecondsRemaining > 0 && (
                <span 
                  className="text-[10px] px-2 py-0.5 rounded-full bg-astro-bg border border-astro-border text-astro-secondary-text flex items-center gap-1 shadow-2xs"
                  title={isHi ? 'पर्याप्त अध्ययन समय पश्चात स्वतः संक्षिप्त हो जाएगा (माउस रखने पर समय रुकेगा)' : 'Auto-collapses after reading duration (pauses on mouse hover)'}
                >
                  <Timer className="w-3 h-3 text-astro-accent shrink-0" />
                  <span>
                    {isGuideHovered 
                      ? (isHi ? 'अध्ययन जारी (रुका हुआ)' : 'Paused (Reading)') 
                      : `${isHi ? 'स्वतः संक्षेप' : 'Auto-collapse'}: ${guideSecondsRemaining}s`}
                  </span>
                </span>
              )}

              <button
                onClick={() => {
                  setHasUserManuallyToggledGuide(true);
                  setIsGuideExpanded(!isGuideExpanded);
                }}
                className="px-2.5 py-1 rounded-md text-[11px] font-bold border border-astro-border bg-astro-bg hover:bg-[#FAF6ED] text-astro-primary flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                title={isGuideExpanded ? (isHi ? 'गाइड संक्षिप्त करें' : 'Collapse Guide') : (isHi ? 'गाइड विस्तार करें' : 'Expand Guide')}
              >
                <span>{isGuideExpanded ? (isHi ? 'संक्षिप्त करें' : 'Collapse') : (isHi ? 'विस्तार करें' : 'Expand')}</span>
                {isGuideExpanded ? <ChevronUp className="w-3.5 h-3.5 text-astro-secondary-text" /> : <ChevronDown className="w-3.5 h-3.5 text-astro-secondary-text" />}
              </button>
            </div>
          </div>

          {/* When collapsed: compact preview of color dots and motion categories */}
          {!isGuideExpanded && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-[11px] border-t border-astro-border/50">
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 font-semibold text-emerald-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                  <span>{isHi ? 'शुभ तारा (5): सम्पत, क्षेम, साधक, मित्र, अति-मित्र' : 'Benefic Stars (5): Sampat, Kshema, Sadhana, Mitra, Ati-Mitra'}</span>
                </span>
                <span className="flex items-center gap-1.5 font-semibold text-rose-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0" />
                  <span>{isHi ? 'अशुभ तारा (3): विपत, प्रत्यरि, वध' : 'Malefic Stars (3): Vipat, Pratyari, Vadha'}</span>
                </span>
                <span className="flex items-center gap-1.5 font-semibold text-amber-900">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                  <span>{isHi ? 'जन्म तारा (1): देह व मन' : 'Janma Star (1): Body & Mind'}</span>
                </span>
              </div>
              <button
                onClick={() => {
                  setHasUserManuallyToggledGuide(true);
                  setIsGuideExpanded(true);
                }}
                className="text-[11px] font-bold text-astro-accent hover:text-astro-primary transition-colors cursor-pointer flex items-center gap-0.5"
              >
                <span>{isHi ? 'पूर्ण गाइड व गति वर्गीकरण देखें →' : 'Read Full Guide & Motion Types →'}</span>
              </button>
            </div>
          )}

          {/* When expanded: full color legend + motion taxonomy */}
          {isGuideExpanded && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
                  <div>
                    <span className="font-bold">{isHi ? 'शुभ तारा (Benefic)' : 'Benefic Stars (Shubha)'}:</span>
                    <span className="block font-normal text-[10px] text-emerald-800">Sampat, Kshema, Sadhana / Sadhaka, Mitra, Ati-Mitra</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0"></span>
                  <div>
                    <span className="font-bold">{isHi ? 'अशुभ तारा (Malefic)' : 'Malefic Stars (Ashubha)'}:</span>
                    <span className="block font-normal text-[10px] text-rose-800">Vipat (Danger), Pratyari (Obstacles), Vadha (Severe)</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0"></span>
                  <div>
                    <span className="font-bold">{isHi ? 'संवेदनशील (Janma)' : 'Sensitive Star (Janma)'}:</span>
                    <span className="block font-normal text-[10px] text-amber-800">Janma (Body, Vitality - Introspection)</span>
                  </div>
                </div>
              </div>

              {/* Nakshatra Motion Nature Taxonomy (Chala / Achala / Dvisvabhava / Kshipra / Ugra / Tikshna) */}
              <div className="pt-2 border-t border-astro-border/60">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block mb-1.5">
                  {isHi ? 'शास्त्रीय नक्षत्र गति वर्गीकरण (Motion Taxonomy)' : 'Classical Nakshatra Motion Classification'}
                </span>
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  <span className="px-2 py-0.5 rounded bg-sky-50 text-sky-900 border border-sky-200 font-semibold flex items-center gap-1">
                    <Wind className="w-2.5 h-2.5 text-sky-600" />
                    <b>{isHi ? 'चल / चर' : 'Chara (Movable)'}</b>: Punarvasu, Swati, Shravana, Dhanishta, Shatabhisha
                  </span>
                  <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-200 font-semibold flex items-center gap-1">
                    <Anchor className="w-2.5 h-2.5 text-emerald-600" />
                    <b>{isHi ? 'स्थिर / अचल' : 'Sthira (Fixed)'}</b>: Rohini, Uttara Phalguni, Uttara Ashadha, Uttara Bhadrapada
                  </span>
                  <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-900 border border-purple-200 font-semibold flex items-center gap-1">
                    <Activity className="w-2.5 h-2.5 text-purple-600" />
                    <b>{isHi ? 'द्वि-स्वभाव / मृदु' : 'Dvisvabhava / Mridu'}</b>: Mrigashira, Chitra, Anuradha, Revati
                  </span>
                  <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 font-semibold flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5 text-amber-600" />
                    <b>{isHi ? 'लघु / क्षिप्र' : 'Kshipra / Laghu'}</b>: Ashwini, Pushya, Hasta
                  </span>
                  <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-900 border border-rose-200 font-semibold flex items-center gap-1">
                    <Flame className="w-2.5 h-2.5 text-rose-600" />
                    <b>{isHi ? 'उग्र / तीक्ष्ण' : 'Ugra & Tikshna'}</b>: Bharani, Magha, P.Phalguni, P.Ashadha, P.Bhadra, Ardra, Ashlesha, Jyeshtha, Mula
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Comprehensive 9-Fold Navtara Matrix Table (with integrated Filter View & Collapsible Controls) */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl shadow-xs overflow-hidden">
        {/* Unified Table Header with Integrated Filter Bar */}
        <div className="p-4 border-b border-astro-border bg-astro-card space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-astro-primary/10 text-astro-primary flex items-center justify-center shrink-0">
                <Layers className="w-4 h-4 text-astro-accent" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-base text-astro-primary flex items-center gap-2">
                  <span>{isHi ? 'नव तारा 9×3 चक्र सारणी' : 'Navtara 9×3 Chakra Matrix Table'}</span>
                  <span className="text-xs font-sans font-normal text-astro-secondary-text">
                    ({isHi ? '27 नक्षत्र • 3 चक्र' : '27 Nakshatras • 3 Cycles'})
                  </span>
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Hide Current Nakshatra from Table Toggle */}
              <button
                type="button"
                onClick={() => handleToggleHideCurrentInTable(!hideCurrentInTable)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs ${
                  hideCurrentInTable
                    ? 'bg-astro-primary text-white border-astro-primary'
                    : 'bg-astro-bg text-astro-secondary-text border-astro-border hover:bg-[#FAF6ED]'
                }`}
                title={isHi ? 'सारणी में से वर्तमान सक्रिय नक्षत्र को छिपाएं' : 'Hide Current Nakshatra from matrix table'}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{isHi ? 'वर्तमान नक्षत्र छिपाएं' : 'Hide Current Star'}: {hideCurrentInTable ? (isHi ? 'हां' : 'ON') : (isHi ? 'नहीं' : 'OFF')}</span>
              </button>

              {/* Collapse/Expand Toggle Button */}
              <button
                type="button"
                onClick={handleToggleTableExpand}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-astro-border bg-astro-bg hover:bg-[#FAF6ED] text-astro-primary flex items-center gap-1.5 cursor-pointer transition-all shadow-2xs"
                title={isTableExpanded ? (isHi ? 'सारणी संक्षिप्त करें' : 'Collapse Navtara Table') : (isHi ? 'सारणी विस्तार करें' : 'Expand Navtara Table')}
              >
                <span>{isTableExpanded ? (isHi ? 'सारणी संक्षिप्त करें' : 'Collapse') : (isHi ? 'सारणी विस्तार करें' : 'Expand Table')}</span>
                {isTableExpanded ? <ChevronUp className="w-4 h-4 text-astro-secondary-text" /> : <ChevronDown className="w-4 h-4 text-astro-secondary-text" />}
              </button>
            </div>
          </div>

          {/* Integrated Filter Controls (Visible when Table is Expanded) */}
          {isTableExpanded && (
            <div className="pt-2 border-t border-astro-border/70 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-astro-primary flex items-center gap-1">
                  <Compass className="w-3.5 h-3.5 text-astro-accent" />
                  {isHi ? 'चक्र प्रदर्शन' : 'Filter View'}:
                </span>
                <div className="inline-flex rounded-lg border border-astro-border bg-astro-bg p-0.5">
                  <button
                    type="button"
                    onClick={() => setSelectedCycleFilter('all')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                      selectedCycleFilter === 'all'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    <Layers className="w-3.5 h-3.5 shrink-0" />
                    <span>{isHi ? 'सभी 3 चक्र (1-3)' : 'All 3 Cycles'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedCycleFilter('cycle1')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                      selectedCycleFilter === 'cycle1'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    {isHi ? 'चक्र 1 (प्रत्यक्ष)' : 'Cycle 1 (1-9)'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedCycleFilter('cycle2')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                      selectedCycleFilter === 'cycle2'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    {isHi ? 'चक्र 2 (मध्यम)' : 'Cycle 2 (10-18)'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedCycleFilter('cycle3')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                      selectedCycleFilter === 'cycle3'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    {isHi ? 'चक्र 3 (गंभीर)' : 'Cycle 3 (19-27)'}
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-astro-secondary-text flex items-center gap-2">
                <span className="font-semibold">{isHi ? 'गति प्रकृति' : 'Nature'}:</span>
                <select
                  value={selectedNatureFilter}
                  onChange={(e) => setSelectedNatureFilter(e.target.value as any)}
                  className="px-2.5 py-1 rounded-md border border-astro-border bg-astro-bg text-astro-primary text-xs font-medium focus:outline-hidden"
                >
                  <option value="all">{isHi ? 'सभी प्रकृतियां (All Nature)' : 'All Motion Natures'}</option>
                  <option value="chara">{isHi ? 'चर / चल (Chara - Movable)' : 'Chara (Movable)'}</option>
                  <option value="sthira">{isHi ? 'स्थिर / अचल (Sthira - Fixed)' : 'Sthira (Fixed)'}</option>
                  <option value="dvisvabhava">{isHi ? 'द्वि-स्वभाव (Dvisvabhava - Dual)' : 'Dvisvabhava (Dual)'}</option>
                  <option value="kshipra">{isHi ? 'लघु / क्षिप्र (Kshipra - Swift)' : 'Kshipra (Swift)'}</option>
                  <option value="ugra">{isHi ? 'उग्र / क्रूर (Ugra - Fierce)' : 'Ugra (Fierce)'}</option>
                  <option value="tikshna">{isHi ? 'तीक्ष्ण / दारुण (Tikshna - Sharp)' : 'Tikshna (Sharp)'}</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* When collapsed: show clean single-row prompt */}
        {!isTableExpanded && (
          <div className="p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3 bg-astro-bg text-xs">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-astro-primary/10 text-astro-primary flex items-center justify-center shrink-0">
                <Layers className="w-5 h-5 text-astro-accent" />
              </div>
              <div>
                <p className="font-bold text-astro-primary text-sm">
                  {isHi ? 'नव तारा सारणी संक्षिप्त है' : 'Navtara Matrix Table is Collapsed'}
                </p>
                <p className="text-astro-secondary-text text-[11px] mt-0.5">
                  {isHi 
                    ? '27 नक्षत्रों का 9-चरणीय चक्र व स्वामी संबंध देखने हेतु सारणी खोलें'
                    : 'Expand to view the 9×3 planetary matrix with Tara alignments and motion natures'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleTableExpand}
              className="px-3.5 py-2 rounded-lg bg-astro-primary text-white text-xs font-bold hover:bg-opacity-90 transition-all cursor-pointer shadow-xs flex items-center gap-1.5"
            >
              <span>{isHi ? 'पूर्ण 27 नक्षत्र सारणी देखें' : 'View Full Matrix Table'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* When expanded: show full table */}
        {isTableExpanded && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-astro-primary text-white text-xs uppercase tracking-wider">
                  <th className="py-3.5 px-4 font-bold min-w-[170px] border-r-2 border-white/30 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                    {isHi ? 'तारा (Tara)' : 'Tara / Quality'}
                  </th>
                  {(selectedCycleFilter === 'all' || selectedCycleFilter === 'cycle1') && (
                    <th className="py-3.5 px-4 font-bold min-w-[210px]">{isHi ? 'चक्र 1 (प्रत्यक्ष 1-9)' : 'Cycle 1 (1-9)'}</th>
                  )}
                  {(selectedCycleFilter === 'all' || selectedCycleFilter === 'cycle2') && (
                    <th className="py-3.5 px-4 font-bold min-w-[210px]">{isHi ? 'चक्र 2 (मध्यम 10-18)' : 'Cycle 2 (10-18)'}</th>
                  )}
                  {(selectedCycleFilter === 'all' || selectedCycleFilter === 'cycle3') && (
                    <th className="py-3.5 px-4 font-bold min-w-[210px]">{isHi ? 'चक्र 3 (गंभीर 19-27)' : 'Cycle 3 (19-27)'}</th>
                  )}
                  <th className="py-3.5 px-4 font-bold min-w-[90px]">{isHi ? 'स्वामी' : 'Lord'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border text-sm">
                {TARA_DEFINITIONS.map((taraDef, i) => {
                  const nak1Idx = (janmaIndex + i) % 27;
                  const nak2Idx = (janmaIndex + i + 9) % 27;
                  const nak3Idx = (janmaIndex + i + 18) % 27;

                  const nak1 = NAKSHATRAS[nak1Idx];
                  const nak2 = NAKSHATRAS[nak2Idx];
                  const nak3 = NAKSHATRAS[nak3Idx];

                  const class1 = NAKSHATRA_CLASSIFICATIONS[nak1Idx];
                  const class2 = NAKSHATRA_CLASSIFICATIONS[nak2Idx];
                  const class3 = NAKSHATRA_CLASSIFICATIONS[nak3Idx];

                  const status1 = getNakshatraTransitStatus(nak1Idx, taraDef.type);
                  const status2 = getNakshatraTransitStatus(nak2Idx, taraDef.type);
                  const status3 = getNakshatraTransitStatus(nak3Idx, taraDef.type);

                  const hasActiveInRow = status1.active || status2.active || status3.active;
                  const rowLord = nak1.lord;

                  // Motion filter check
                  const match1 = selectedNatureFilter === 'all' || class1.motionType === selectedNatureFilter;
                  const match2 = selectedNatureFilter === 'all' || class2.motionType === selectedNatureFilter;
                  const match3 = selectedNatureFilter === 'all' || class3.motionType === selectedNatureFilter;

                  if (selectedNatureFilter !== 'all' && !match1 && !match2 && !match3) {
                    return null;
                  }

                  // Non-vibrant, harmonious color classes for upcoming badges based on Sthira & Ugra palettes
                  const getStatusChipClasses = (status: ReturnType<typeof getNakshatraTransitStatus>) => {
                    if (status.active) {
                      return 'bg-amber-100 text-amber-950 border border-amber-400 font-bold';
                    }

                    // Upcoming / Next transits
                    if (taraDef.type === 'benefic') {
                      return 'bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 font-semibold';
                    }
                    if (taraDef.type === 'malefic') {
                      return 'bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100 font-semibold';
                    }
                    return 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 font-semibold';
                  };

                  const renderNakshatraCell = (
                    nakIdx: number,
                    nakObj: (typeof NAKSHATRAS)[number],
                    nakClass: (typeof NAKSHATRA_CLASSIFICATIONS)[number],
                    status: ReturnType<typeof getNakshatraTransitStatus>,
                    matchNature: boolean
                  ) => {
                    return (
                      <td className={`py-3.5 px-3.5 align-top transition-all ${status.active ? (taraDef.type === 'benefic' ? 'bg-emerald-50/60' : taraDef.type === 'malefic' ? 'bg-rose-50/60' : 'bg-amber-50/60') : ''} ${!matchNature ? 'opacity-40' : ''}`}>
                        {status.active ? (
                          <div 
                            onClick={handleDismissFlashSession}
                            className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${
                              taraDef.type === 'benefic'
                                ? 'border-emerald-500 bg-emerald-50/95 shadow-sm ring-2 ring-emerald-400/30'
                                : taraDef.type === 'malefic'
                                ? 'border-rose-500 bg-rose-50/95 shadow-sm ring-2 ring-rose-400/30'
                                : 'border-amber-500 bg-amber-50/95 shadow-sm ring-2 ring-amber-400/30'
                            } ${
                              isCurrentlyFlashing && !sessionFlashDismissed
                                ? 'animate-nakshatra-flash ring-4 scale-[1.01]'
                                : ''
                            }`}
                            title={isHi ? 'वर्तमान सक्रिय गोचर (क्लिक कर स्पंदन बंद करें)' : 'Active Transit in Table (Click to stop flashing)'}
                          >
                            <div className="flex items-center justify-between gap-1.5 flex-wrap">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white shadow-2xs ${
                                taraDef.type === 'benefic' ? 'bg-emerald-600' : taraDef.type === 'malefic' ? 'bg-rose-600' : 'bg-amber-600'
                              }`}>
                                <span className="relative flex h-2 w-2 items-center justify-center shrink-0">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                                </span>
                                <span className="animate-pulse">{isHi ? 'सक्रिय गोचर' : 'ACTIVE'}</span>
                              </span>
                              {showMotionBadges && getMotionChip(nakClass?.motionType, true)}
                            </div>

                            <NakshatraHoverCard
                              nakIndex={nakIdx}
                              janmaIndex={janmaIndex}
                              isHi={isHi}
                              kundali={kundali}
                              enabled={hoverEnabled}
                            >
                              <div className="font-extrabold text-astro-primary text-sm sm:text-base cursor-help hover:text-astro-accent transition-colors mt-1">
                                #{nakIdx + 1} {getNakshatraName(nakObj.name, isHi ? 'hi' : 'en')}
                              </div>
                            </NakshatraHoverCard>

                            {showLordDeityDetails && (
                              <div className="text-[11px] text-astro-secondary-text mt-0.5">
                                <span className="font-bold text-astro-primary mr-1">
                                  {isHi ? 'स्वामी:' : 'Lord:'} {nakObj.lord} •
                                </span>
                                <span>{isHi ? nakClass.deityHi : nakClass.deity}</span>
                              </div>
                            )}

                            <div className={`mt-1.5 pt-1 border-t flex items-center justify-between gap-1 text-[11px] font-bold ${
                              taraDef.type === 'benefic' ? 'border-emerald-300/60 text-emerald-950' : taraDef.type === 'malefic' ? 'border-rose-300/60 text-rose-950' : 'border-amber-300/60 text-amber-950'
                            }`}>
                              <span className="flex items-center gap-1">
                                <Clock className={`w-3 h-3 shrink-0 ${taraDef.type === 'benefic' ? 'text-emerald-700' : taraDef.type === 'malefic' ? 'text-rose-700' : 'text-amber-700'}`} />
                                <span>{isHi ? 'समाप्ति:' : 'Until:'} {status.endStr}</span>
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.2 rounded font-black ${
                                taraDef.type === 'benefic' ? 'bg-emerald-200/80 text-emerald-900' : taraDef.type === 'malefic' ? 'bg-rose-200/80 text-rose-900' : 'bg-amber-200/80 text-amber-900'
                              }`}>
                                {grossHours}h {grossMins}m
                              </span>
                            </div>
                          </div>
                        ) : (
                          <>
                            <NakshatraHoverCard
                              nakIndex={nakIdx}
                              janmaIndex={janmaIndex}
                              isHi={isHi}
                              kundali={kundali}
                              enabled={hoverEnabled}
                            >
                              <div className="font-bold text-astro-text text-sm cursor-help hover:text-astro-accent transition-colors flex items-center gap-1.5 flex-wrap">
                                <span>#{nakIdx + 1} {getNakshatraName(nakObj.name, isHi ? 'hi' : 'en')}</span>
                                {showMotionBadges && getMotionChip(nakClass?.motionType, true)}
                              </div>
                            </NakshatraHoverCard>

                            {showLordDeityDetails && (
                              <div className="text-[11px] text-astro-secondary-text mt-0.5">
                                <span className="font-semibold text-astro-primary mr-1">
                                  {isHi ? 'स्वामी:' : 'Lord:'} {nakObj.lord} •
                                </span>
                                <span>{isHi ? nakClass.deityHi : nakClass.deity}</span>
                              </div>
                            )}
                            
                            <div className="mt-1.5">
                              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] transition-all text-left ${getStatusChipClasses(status)}`}>
                                <span>{status.label}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </td>
                    );
                  };

                  return (
                    <tr 
                      key={taraDef.name} 
                      className={`transition-colors ${
                        hasActiveInRow 
                          ? taraDef.type === 'benefic' 
                            ? 'bg-emerald-50/40 hover:bg-emerald-50/70' 
                            : taraDef.type === 'malefic'
                            ? 'bg-rose-50/40 hover:bg-rose-50/70'
                            : 'bg-amber-50/40 hover:bg-amber-50/70'
                          : 'hover:bg-[#FAF8F5]'
                      }`}
                    >
                      {/* TARA COLUMN with vertical bar distinguishing it from rest */}
                      <td className="py-4 px-4 align-top border-r-2 border-astro-strong-border bg-astro-card/35">
                        <div className="flex gap-2.5">
                          {/* Vertical indicator bar for Tara classification */}
                          <div className={`w-1 self-stretch rounded-full shrink-0 ${
                            taraDef.type === 'benefic' 
                              ? 'bg-emerald-600' 
                              : taraDef.type === 'malefic' 
                              ? 'bg-rose-600' 
                              : 'bg-amber-600'
                          }`} />

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-astro-primary text-base">
                                {isHi ? taraDef.nameHi : taraDef.name}
                              </span>
                              <button
                                onClick={() => setSelectedTara(taraDef)}
                                title={isHi ? taraDef.meaningHi : taraDef.meaning}
                                className="text-astro-accent hover:text-astro-primary transition-colors cursor-pointer"
                              >
                                <HelpCircle className="w-4 h-4 cursor-help" />
                              </button>
                            </div>

                            <div className="mt-1">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold border shadow-2xs ${
                                taraDef.type === 'benefic'
                                  ? 'bg-emerald-100 text-emerald-950 border-emerald-300'
                                  : taraDef.type === 'malefic'
                                  ? 'bg-rose-100 text-rose-950 border-rose-300'
                                  : 'bg-amber-100 text-amber-950 border-amber-300'
                              }`}>
                                <span className="relative flex h-1.5 w-1.5 items-center justify-center shrink-0">
                                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                                    taraDef.type === 'benefic' ? 'bg-emerald-500' : taraDef.type === 'malefic' ? 'bg-rose-500' : 'bg-amber-500'
                                  }`} />
                                  <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                                    taraDef.type === 'benefic' ? 'bg-emerald-600' : taraDef.type === 'malefic' ? 'bg-rose-600' : 'bg-amber-600'
                                  }`} />
                                </span>
                                <span className="animate-pulse font-extrabold">{isHi ? taraDef.typeLabelHi : taraDef.typeLabel}</span>
                              </span>
                            </div>

                            <div className="text-[11px] text-astro-secondary-text mt-1 line-clamp-1">
                              {isHi ? taraDef.meaningHi : taraDef.meaning}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* CYCLE 1 */}
                      {(selectedCycleFilter === 'all' || selectedCycleFilter === 'cycle1') && 
                        renderNakshatraCell(nak1Idx, nak1, class1, status1, match1)
                      }

                      {/* CYCLE 2 */}
                      {(selectedCycleFilter === 'all' || selectedCycleFilter === 'cycle2') && 
                        renderNakshatraCell(nak2Idx, nak2, class2, status2, match2)
                      }

                      {/* CYCLE 3 */}
                      {(selectedCycleFilter === 'all' || selectedCycleFilter === 'cycle3') && 
                        renderNakshatraCell(nak3Idx, nak3, class3, status3, match3)
                      }

                      {/* NAKSHATRA LORD COLUMN */}
                      <td className="py-4 px-4 align-top font-bold text-astro-primary">
                        {rowLord}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Special Sensitive Nakshatras Reference Card (Optional / Collapsible) */}
      {showSensitivePoints && (
        <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'विशिष्ट संवेदनशील नक्षत्र (Special Sensitive Points)' : 'Special Sensitive Nakshatra Points (Parashari)'}</span>
              </h3>
              <span className="text-[11px] text-astro-secondary-text hidden sm:inline">
                • {isHi ? 'जन्म नक्षत्र से विशिष्ट दूरी' : 'Calculated by offset from Janma Nakshatra'}
              </span>
            </div>

          <button
            onClick={handleToggleSensitiveExpand}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold border border-astro-border bg-astro-bg hover:bg-[#FAF6ED] text-astro-primary flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
            title={isSensitiveExpanded ? (isHi ? 'संवेदनशील नक्षत्र संक्षिप्त करें' : 'Collapse Sensitive Points') : (isHi ? 'संवेदनशील नक्षत्र विस्तार करें' : 'Expand Sensitive Points')}
          >
            <span>{isSensitiveExpanded ? (isHi ? 'संक्षिप्त करें' : 'Collapse') : (isHi ? 'विस्तार करें' : 'Expand')}</span>
            {isSensitiveExpanded ? <ChevronUp className="w-3.5 h-3.5 text-astro-secondary-text" /> : <ChevronDown className="w-3.5 h-3.5 text-astro-secondary-text" />}
          </button>
        </div>

        {/* When collapsed: show quick chips bar */}
        {!isSensitiveExpanded && (
          <div className="flex flex-wrap gap-2 pt-1 border-t border-astro-border/50 text-[11px]">
            {SPECIAL_SENSITIVE_NAKSHATRAS.map((sens) => {
              const targetNakIdx = (janmaIndex + sens.offsetFromJanma) % 27;
              const targetNak = NAKSHATRAS[targetNakIdx];
              const isCurrentlyActive = currentMoonNakIndex === targetNakIdx;

              return (
                <div 
                  key={sens.roleId}
                  className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 ${
                    isCurrentlyActive
                      ? 'bg-amber-100 border-amber-400 font-bold text-amber-950'
                      : 'bg-astro-bg border-astro-border text-astro-primary'
                  }`}
                >
                  <span className="font-bold opacity-80">{isHi ? sens.badgeLabelHi : sens.badgeLabelEn}:</span>
                  <span>#{targetNakIdx + 1} {getNakshatraName(targetNak.name, isHi ? 'hi' : 'en')}</span>
                  {isCurrentlyActive && (
                    <span className="px-1 py-0.2 rounded text-[8px] bg-amber-600 text-white font-extrabold">
                      ACTIVE
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* When expanded: show the full 8-card grid */}
        {isSensitiveExpanded && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SPECIAL_SENSITIVE_NAKSHATRAS.map((sens) => {
              const targetNakIdx = (janmaIndex + sens.offsetFromJanma) % 27;
              const targetNak = NAKSHATRAS[targetNakIdx];
              const targetClass = NAKSHATRA_CLASSIFICATIONS[targetNakIdx];
              const isCurrentlyActive = currentMoonNakIndex === targetNakIdx;

              return (
                <NakshatraHoverCard
                  key={sens.roleId}
                  nakIndex={targetNakIdx}
                  janmaIndex={janmaIndex}
                  isHi={isHi}
                  enabled={hoverEnabled}
                  className="w-full"
                >
                  <div className={`p-3.5 rounded-xl border text-xs space-y-1.5 transition-all ${
                    isCurrentlyActive
                      ? 'bg-amber-100/90 border-amber-500 ring-2 ring-amber-400/30'
                      : 'bg-astro-bg border-astro-border hover:bg-[#FAF6ED]'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-extrabold text-astro-primary truncate">
                        {isHi ? sens.badgeLabelHi : sens.badgeLabelEn} (#{sens.starNumber})
                      </span>
                      {isCurrentlyActive && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-600 text-white font-black">
                          ACTIVE
                        </span>
                      )}
                    </div>

                    <div className="font-bold text-sm text-astro-text">
                      #{targetNakIdx + 1} {getNakshatraName(targetNak.name, isHi ? 'hi' : 'en')}
                    </div>

                    <p className="text-[10px] text-astro-secondary-text leading-tight line-clamp-2">
                      {isHi ? sens.significanceHi : sens.significanceEn}
                    </p>
                  </div>
                </NakshatraHoverCard>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Selected Tara Detail Modal */}
      {selectedTara && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-astro-text/40 backdrop-blur-xs animate-fade-in"
          onClick={() => setSelectedTara(null)}
        >
          <div 
            className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto text-astro-text p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold font-serif text-astro-primary">
                  {isHi ? selectedTara.nameHi : selectedTara.name} Tara
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  selectedTara.type === 'benefic'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : selectedTara.type === 'malefic'
                    ? 'bg-rose-100 text-rose-800 border border-rose-300'
                    : 'bg-amber-100 text-amber-800 border border-amber-300'
                }`}>
                  {isHi ? selectedTara.typeLabelHi : selectedTara.typeLabel}
                </span>
              </div>
              <button 
                onClick={() => setSelectedTara(null)}
                className="text-astro-secondary-text hover:text-astro-text text-sm font-bold px-2 py-1 rounded-md hover:bg-astro-card cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text block">
                  {isHi ? 'तात्विक अर्थ (Significance)' : 'Core Significance'}
                </span>
                <p className="font-semibold text-astro-primary mt-0.5">
                  {isHi ? selectedTara.meaningHi : selectedTara.meaning}
                </p>
              </div>

              <div className="bg-astro-card p-3.5 rounded-xl border border-astro-border">
                <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text block">
                  {isHi ? 'ज्योतिषीय परामर्श व सावधानी' : 'Astrological Recommendation & Guidelines'}
                </span>
                <p className="text-xs text-astro-text mt-1 leading-relaxed">
                  {isHi ? selectedTara.recommendationHi : selectedTara.recommendation}
                </p>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setSelectedTara(null)}
                className="px-4 py-2 rounded-xl bg-astro-primary text-white font-bold text-xs hover:bg-astro-secondary transition-colors cursor-pointer"
              >
                {isHi ? 'बंद करें' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
